<div class="max-w-7xl mx-auto px-0 sm:px-4 mt-4 pb-12 w-full">

  <!-- Header Section -->
  <div class="flex flex-row justify-between items-center mb-6 w-full gap-2">
    <h1 class="text-2xl font-semibold text-blueGray-700 tracking-tight">Home</h1>
    <div class="flex items-center justify-end gap-2 flex-shrink-0">
      <!-- Small Screens (< md): app-btn-fab buttons with transparent bg & grey on hover -->
      <div class="flex md:hidden items-center gap-1">
        <app-btn-fab routerLink="search" icon="fas fa-search" size="sm"
          bgColor="bg-transparent hover:bg-gray-200 active:bg-gray-300" textColor="text-primary-500"
          tooltip="Search Communities"></app-btn-fab>
        <app-btn-fab routerLink="create" icon="fas fa-plus" size="sm"
          bgColor="bg-transparent hover:bg-gray-200 active:bg-gray-300" textColor="text-secondary-500"
          tooltip="Create Community"></app-btn-fab>
        <app-btn-fab routerLink="/organizations/request" icon="fas fa-sitemap" size="sm"
          bgColor="bg-transparent hover:bg-gray-200 active:bg-gray-300" textColor="text-primary-500"
          tooltip="Create Organization"></app-btn-fab>
      </div>

      <!-- Medium & Large Screens (>= md): Full Buttons with Text -->
      <div class="hidden md:flex items-center gap-2">
        <app-btn-route colors="bg-primary-500 hover:bg-primary-600" btnText="Search Communities" routeTo="search"
          icon="fa fa-search"></app-btn-route>
        <app-btn-route colors="bg-secondary-500 hover:bg-secondary-600" btnText="Create Community" routeTo="create"
          icon="fa fa-add"></app-btn-route>
        <!-- Organizations are created on request and approved by an AtChurch admin -->
        <app-btn-route colors="bg-primary-400 hover:bg-primary-500" btnText="Create Organization"
          routeTo="/organizations/request" icon="fas fa-sitemap"></app-btn-route>
      </div>
    </div>
  </div>

  <!-- Bento Grid (Compact Viewport heights) -->
  <div class="grid grid-cols-12 w-full gap-4 mb-6">

    <!-- Card 1: Maps view of communities around me (Span 8, height 300px) -->
    <div
      class="col-span-full lg:col-span-7 w-full flex flex-col bg-white rounded-xl border border-blueGray-200 shadow-sm overflow-hidden h-[300px]">

      <!-- <h3 class="font-bold text-blueGray-700 text-sm px-4 pt-4 pb-2">Communities Around Me</h3> -->

      <!-- Map container -->
      <div class="h-full relative">
        <div [class]="'w-full h-full ' + (nearbyGroups.length === 0 ? 'filter grayscale opacity-40' : '')">
          <google-map class="h-full w-full" width="784px" [options]="mapOptions" [center]="mapCenter">
            @if (userLocation) {
            <map-advanced-marker [position]="userLocation" [content]="userLocationMarkerContent"
              title="Your Current Location" />
            }
            @for (marker of markers; track marker.id) {
            <map-advanced-marker #mapMarker="mapAdvancedMarker" [position]="marker.position" [content]="marker.content"
              [title]="marker.title" (mapClick)="selectGroup(marker.group, mapMarker)" />
            }
            <map-info-window>
              @if (infoWindowGroup) {
              <div class="max-w-[240px] text-xs">
                <div class="flex items-center gap-2 mb-2">
                  <div class="flex-shrink-0">
                    @if (infoWindowGroup.imageUrl) {
                    <img [src]="infoWindowGroup.imageUrl"
                      class="h-8 w-8 rounded-full object-cover border border-blueGray-200" />
                    } @else {
                    <div
                      class="h-8 w-8 rounded-full bg-primary-100 text-primary-500 font-bold flex items-center justify-center text-[10px]">
                      {{infoWindowGroup.name | initials}}
                    </div>
                    }
                  </div>
                  <div class="min-w-0">
                    <h4 class="font-bold text-blueGray-800 truncate">{{infoWindowGroup.name}}</h4>
                    @if (infoWindowGroup.address?.city; as city) {
                    <p class="text-[10px] text-blueGray-500 truncate">{{city}}</p>
                    }
                  </div>
                </div>
                @if (infoWindowGroup.description) {
                <p class="text-[10px] text-blueGray-600 line-clamp-2 mb-2">{{infoWindowGroup.description}}</p>
                }
                <div class="flex items-center justify-between pt-1 border-t border-blueGray-100 mt-2">
                  <span
                    class="text-[9px] capitalize px-1.5 py-0.5 rounded-full bg-blueGray-100 text-blueGray-600 font-medium">
                    {{infoWindowGroup.groupType}}
                  </span>
                  <button (click)="openDetailModal(infoWindowGroup)"
                    class="text-[10px] text-primary-500 hover:text-primary-700 font-bold flex items-center border-none outline-none ring-0 focus:outline-none focus:ring-0 gap-1">
                    View <i class="fas fa-chevron-right"></i>
                  </button>
                </div>
              </div>
              }
            </map-info-window>
          </google-map>
        </div>
        <!-- Centered overlay actions if empty -->
        @if (nearbyGroups.length === 0 && !loadingNearby) {
        <div
          class="absolute h-full inset-0 flex flex-col items-center justify-center p-4 bg-white/40 backdrop-blur-[1px]">
          <p class="text-xs font-bold text-primary-500 mb-2.5 bg-white/20 px-3 py-1 rounded-full">No
            communities near you</p>
          <div class="flex items-center gap-3 mb-4">
            <button (click)="router.navigate(['/groups/search'])"
              class="h-9 w-9 rounded-full bg-white/60 hover:bg-primary-500 text-primary-500 hover:text-white shadow-md transition-all flex items-center justify-center border-none focus:ring-0"
              title="Search Communities">
              <i class="fa fa-search text-sm"></i>
            </button>
            <button (click)="router.navigate(['/groups/create'])"
              class="h-9 w-9 rounded-full bg-white/60 hover:bg-primary-500 text-primary-500 hover:text-white shadow-md transition-all flex items-center justify-center border-none focus:ring-0"
              title="Create Community">
              <i class="fas fa-plus text-sm"></i>
            </button>
            <a [href]="whatsAppInviteLink" target="_blank"
              class="h-9 w-9 rounded-full bg-white/60 hover:bg-emerald-500 text-emerald-500 hover:text-white shadow-md transition-all flex items-center justify-center border-none focus:ring-0"
              title="Invite Friends">
              <i class="fab fa-whatsapp text-base"></i>
            </a>
          </div>
          <div class="flex items-center justify-center text-center p-4 bg-blueGray-100/20 no-scrollbar">
            <p class="text-xs text-primary-300 max-w-xs leading-relaxed">
              Start a community in your region or invite friends to join AtChurch!
            </p>
          </div>
        </div>
        }
      </div>

      <!-- Details list (Span 5) -->
      <div class="flex flex-col h-full overflow-hidden border-t md:border-t-0 md:border-l border-blueGray-100">
        @if (loadingNearby) {
        <div class="flex-1 overflow-y-auto p-4 space-y-3 animate-pulse">
          <div class="h-14 bg-blueGray-100 rounded-xl"></div>
          <div class="h-14 bg-blueGray-100 rounded-xl"></div>
        </div>
        } @else {
        @if (nearbyGroups.length > 0) {
        <div class="flex-1 overflow-y-auto no-scrollbar p-3 space-y-2">
          @for (item of nearbyGroups; track item.id) {
          <div (click)="selectGroup(item)"
            [class]="'flex items-center justify-between p-2.5 rounded-xl border cursor-pointer transition-all ' + 
                                (selectedGroup?.id === item.id ? 'bg-primary-50 border-primary-200 shadow-sm' : 'bg-white border-blueGray-100 hover:bg-blueGray-50')">
            <div class="flex items-center gap-2.5 min-w-0">
              <div class="flex-shrink-0">
                @if (item.imageUrl) {
                <img [src]="item.imageUrl" class="h-9 w-9 rounded-full object-cover border border-blueGray-200" />
                } @else {
                <div
                  class="h-9 w-9 rounded-full bg-primary-100 text-primary-500 font-bold flex items-center justify-center text-xs">
                  {{item.name | initials}}
                </div>
                }
              </div>
              <div class="min-w-0">
                <h4 class="font-bold text-blueGray-800 text-xs truncate">{{item.name}}</h4>
                <p class="text-[10px] text-blueGray-500 truncate mt-0.5">{{item.address?.city}}</p>
              </div>
            </div>
            <button (click)="$event.stopPropagation(); onClick(item)"
              class="text-primary-500 hover:text-primary-700 p-1 flex items-center justify-center">
              <i class="fas fa-chevron-right text-xs"></i>
            </button>
          </div>
          }
        </div>
        }
        }
      </div>

    </div>

    <!-- Card 2: Events near me (Span 4, height 300px) -->
    <div
      class="col-span-full lg:col-span-5 w-full flex flex-col bg-white rounded-xl border border-blueGray-200 shadow-sm overflow-hidden h-auto lg:h-[300px]">
      <h3 class="font-bold text-blueGray-700 text-sm px-4 pt-4 pb-2 flex items-center gap-2">
        <i class="fas fa-calendar-days text-primary-500 mr-1"></i>Events Near Me
      </h3>

      <div class="flex-1 flex flex-col overflow-hidden">
        @if (loadingEvents) {
        <div class="flex-1 overflow-y-auto p-4 space-y-3 animate-pulse">
          <div class="h-14 bg-blueGray-100 rounded-xl"></div>
          <div class="h-14 bg-blueGray-100 rounded-xl"></div>
        </div>
        } @else {
        @if (nearbyEvents.length > 0) {
        <!-- Desktop List: vertical list of events -->
        <div class="hidden lg:block flex-1 overflow-y-auto no-scrollbar p-2 space-y-2">
          @for (event of nearbyEvents; track event.id) {
          <div (click)="openEventDetailModal(event)"
            class="flex items-center justify-between p-2.5 rounded-xl border border-blueGray-100 hover:border-primary-300 hover:bg-blueGray-50 cursor-pointer transition-all">
            <div class="flex items-center gap-2.5 min-w-0">
              <!-- Date badge -->
              <div
                class="flex-shrink-0 bg-blueGray-50 rounded-lg px-1.5 py-1 flex flex-col items-center justify-center border border-blueGray-100 w-9 h-9">
                <span class="text-[8px] uppercase font-bold text-blueGray-400 leading-none">
                  {{ event.startDateTime | moment: 'MMM' }}
                </span>
                <span class="text-xs font-extrabold text-blueGray-800 leading-none mt-0.5">
                  {{ event.startDateTime | moment: 'D' }}
                </span>
              </div>

              <div class="min-w-0">
                <h4 class="font-bold text-blueGray-800 text-xs truncate">{{event.name}}</h4>
                <div class="flex items-center gap-1.5 mt-0.5 text-[10px] text-blueGray-500 truncate">
                  @if (getGroupForEvent(event)?.imageUrl || event.groupImageUrl || event.bannerImage) {
                  <img [src]="getGroupForEvent(event)?.imageUrl || event.groupImageUrl || event.bannerImage"
                    class="h-3.5 w-3.5 rounded-full object-cover flex-shrink-0 border border-blueGray-200" />
                  } @else {
                  <i class="fas fa-church text-primary-500 text-[9px] flex-shrink-0"></i>
                  }
                  <span class="truncate font-medium text-blueGray-600">
                    {{ getGroupForEvent(event)?.name || event.groupName || 'Community Event' }}
                  </span>
                </div>
              </div>
            </div>
            <i class="fas fa-chevron-right text-blueGray-300 text-xs mr-1"></i>
          </div>
          }
        </div>

        <!-- Mobile List: horizontally scrollable row of events -->
        <div class="block lg:hidden w-full overflow-x-auto no-scrollbar p-4 pt-1">
          <div class="flex flex-row gap-3">
            @for (event of nearbyEvents; track event.id) {
            <div (click)="openEventDetailModal(event)"
              class="w-[calc((100%-24px)/2.5)] flex-shrink-0 flex flex-col cursor-pointer">
              <!-- Square image -->
              <div class="aspect-square w-full rounded-xl overflow-hidden bg-blueGray-100 flex-shrink-0 relative">
                @if (event.bannerImage || event.groupImageUrl || getGroupForEvent(event)?.imageUrl) {
                <img [src]="event.bannerImage || event.groupImageUrl || getGroupForEvent(event)?.imageUrl"
                  class="w-full h-full object-cover" />
                } @else {
                <div
                  class="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100">
                  <i class="fas fa-church text-primary-400 text-xl"></i>
                </div>
                }
              </div>
              <!-- Event Title -->
              <h4 class="font-bold text-blueGray-800 text-xs mt-2 line-clamp-2 leading-snug">{{event.name}}</h4>
              <!-- Church/Group Name -->
              <span class="text-[10px] text-blueGray-500 truncate mt-0.5 leading-tight">
                {{ getGroupForEvent(event)?.name || event.groupName || 'Community Event' }}
              </span>
            </div>
            }
          </div>
        </div>
        } @else {
        <div class="flex-1 flex flex-col items-center justify-center text-center px-5 py-8 lg:p-5">
          <p class="text-xs text-blueGray-500 max-w-xs mb-4">
            No local events found. Would you like to add your Community Event?
          </p>
          <button (click)="onCreateEventClick()"
            class="bg-primary-500 hover:bg-primary-600 text-white font-bold text-xs uppercase rounded-lg px-4 py-2 transition-all shadow hover:shadow-md">
            Add Event
          </button>
        </div>
        }
        }
      </div>
    </div>

  </div>

  <!-- My Communities Section -->
  <div class="mb-4">

    <span class="flex w-full items-center text-start text-md font-semibold text-primary-400 mb-2">My Communities</span>

    <!-- Row-based bento cards grid (icon on left, name/city/badges on right, no enter button) -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
      @for (item of groups$ | async; track item.id) {
      <div (click)="onClick(item)"
        class="bg-white rounded-xl border border-blueGray-100 shadow-sm hover:shadow-md hover:border-gray-300 transition-all p-4 cursor-pointer flex items-start gap-3.5 group">

        <!-- Left side: Group Icon -->
        <div class="flex-shrink-0">
          @if (item.imageUrl) {
          <img [src]="item.imageUrl"
            class="h-12 w-12 rounded-2xl object-cover border border-blueGray-100 group-hover:scale-105 transition-transform" />
          } @else {
          <div
            class="h-12 w-12 rounded-2xl bg-gradient-to-tr from-secondary-100 to-secondary-200 text-secondary-700 font-extrabold flex items-center justify-center text-sm">
            {{item.name | initials}}
          </div>
          }
        </div>

        <!-- Right side: Name, Location, Badges -->
        <div class="min-w-0 flex-1">
          <h3
            class="font-bold text-blueGray-800 group-hover:text-primary-500 transition-colors text-sm truncate leading-snug">
            {{item.name}}
          </h3>

          <p class="text-[11px] font-normal text-gray-500 flex items-center gap-1.5 mt-0.5 truncate">
            <i class="fas fa-location-dot"></i>
            {{item.address?.city || 'Local Region'}}
          </p>

          <!-- Badges -->
          <div class="flex flex-wrap gap-1 mt-1.5">
            @for (role of item.memberType; track role) {
            <span
              class="px-1.5 py-0.5 rounded text-[8px] font-bold uppercase bg-primary-50 text-primary-500 border border-primary-100">
              {{role}}
            </span>
            }
          </div>
        </div>

      </div>
      } @empty {
      <div
        class="col-span-full bg-white rounded-2xl border border-dashed border-blueGray-200 p-8 text-center flex flex-col items-center justify-center">
        <div
          class="h-12 w-12 rounded-full bg-blueGray-50 text-blueGray-400 flex items-center justify-center text-lg mb-3">
          <i class="fas fa-users-slash"></i>
        </div>
        <p class="text-sm font-semibold text-blueGray-600">You haven't joined any communities yet.</p>
      </div>
      }
    </div>
  </div>

  <!-- The user's requests to create an organization (pending, or recently not approved) -->
  @let openOrgRequests = (myOpenOrgRequests$ | async) || [];
  @if (openOrgRequests.length) {
  <div class="mb-6 space-y-2">
    @for (r of openOrgRequests; track r.id) {
    <a routerLink="/organizations/request"
      class="bg-white rounded-xl border border-blueGray-100 shadow-sm hover:shadow-md p-4 flex items-center justify-between gap-3 cursor-pointer">
      <div class="flex items-center gap-3 min-w-0">
        <div class="h-10 w-10 rounded-xl bg-primary-50 text-primary-500 flex items-center justify-center shrink-0">
          <i class="fas fa-sitemap"></i>
        </div>
        <div class="min-w-0">
          <span class="text-sm font-bold text-blueGray-800 block truncate">{{ r.name }}</span>
          <span class="text-[11px] text-blueGray-500">
            @if (r.status === 'pending') { Organization request · waiting for review }
            @else { Organization request not approved{{ r.reviewNote ? ': ' + r.reviewNote : '' }} }
          </span>
        </div>
      </div>
      <span class="px-2 py-0.5 rounded text-[10px] font-bold shrink-0"
        [class]="r.status === 'pending' ? 'bg-amber-50 text-amber-700 border border-amber-100' : 'bg-rose-50 text-rose-700 border border-rose-100'">
        {{ r.status === 'pending' ? 'Pending' : 'Not approved' }}
      </span>
    </a>
    }
  </div>
  }

  <!-- My Management Networks Section -->
  @if ((myManagementNetworks$ | async)?.length) {
  <div class="mb-6">
    <div class="flex items-center justify-between mb-2">
      <span class="text-start text-md font-semibold text-primary-400">
        My Management Networks
      </span>
      <span class="text-xs text-blueGray-400 font-medium">
        Hierarchical Governance
      </span>
    </div>

    <!-- Row-based bento cards grid -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
      @for (network of myManagementNetworks$ | async; track network.organizationId) {
      <div
        [routerLink]="['/management', network.organizationId]"
        class="bg-white rounded-xl border border-blueGray-100 shadow-sm hover:shadow-md hover:border-primary-200 transition-all p-4 cursor-pointer flex items-start gap-3.5 group">
        
        <!-- Left side: Icon -->
        <div class="flex-shrink-0">
          <div class="h-12 w-12 rounded-2xl bg-gradient-to-tr from-primary-50 to-primary-100 text-primary-600 font-extrabold flex items-center justify-center text-lg shadow-2xs group-hover:scale-105 transition-transform">
            <i class="fas fa-sitemap"></i>
          </div>
        </div>

        <!-- Right side: Info & Badges -->
        <div class="min-w-0 flex-1">
          <div class="flex items-center justify-between gap-1">
            <h3 class="font-bold text-blueGray-800 group-hover:text-primary-600 transition-colors text-sm truncate leading-snug">
              {{ network.organizationName || 'Global Fellowship Network' }}
            </h3>
            <span class="px-1.5 py-0.5 rounded text-[8px] font-bold uppercase bg-emerald-50 text-emerald-600 border border-emerald-100">
              {{ network.status }}
            </span>
          </div>

          <p class="text-[11px] font-semibold text-primary-600 flex items-center gap-1.5 mt-0.5 truncate">
            <i class="fas fa-shield-alt text-xs"></i>
            <span class="truncate">{{ network.primaryTitle }}</span>
            @if (network.otherRoleCount > 0) {
              <span class="text-blueGray-400 font-normal shrink-0">+{{ network.otherRoleCount }} more {{ network.otherRoleCount === 1 ? 'role' : 'roles' }}</span>
            }
          </p>

          <div class="flex items-center justify-between pt-2 border-t border-blueGray-100 mt-2 text-[10px] text-blueGray-400">
            <span>Jurisdiction: {{ network.primary.nodeName || ('Level ' + (network.primary.tierLevel ?? '')) }}</span>
            <span class="font-semibold text-primary-500 group-hover:translate-x-0.5 transition-transform flex items-center gap-1">
              Open Hub <i class="fas fa-arrow-right text-[9px]"></i>
            </span>
          </div>
        </div>

      </div>
      }
    </div>
  </div>
  }

</div>

<!-- Community Detail Modal/Popup -->
@if (activeDetailItem) {
<div class="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
  <div
    class="bg-white rounded-2xl border border-blueGray-100 shadow-2xl w-full max-w-lg overflow-hidden flex flex-col relative animate-fade-in">

    <!-- Header Actions row (Close left, Join/Share right) -->
    <div class="flex items-center justify-between p-4 border-b border-blueGray-100 bg-gray-50 flex-none">
      <button (click)="closeDetailModal()"
        class="text-blueGray-400 hover:text-blueGray-600 transition-colors p-1.5 focus:outline-none">
        <i class="fas fa-times text-lg"></i>
      </button>

      <div class="flex items-center gap-2">
        <!-- Share WhatsApp button -->
        <a [href]="getShareLink(activeDetailItem)" target="_blank"
          class="px-3.5 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-full text-xs font-semibold shadow-sm flex items-center gap-1.5 transition-colors">
          <i class="fab fa-whatsapp text-sm"></i> Share
        </a>

        <!-- Join button -->
        <button [disabled]="joinedGroups.has(activeDetailItem.id)" (click)="joinGroup(activeDetailItem)"
          [class]="'px-3.5 py-1.5 rounded-full text-xs font-semibold shadow-sm flex items-center gap-1.5 transition-all ' + 
                             (joinedGroups.has(activeDetailItem.id) ? 'bg-green-50 text-green-700 border border-green-200 cursor-not-allowed' : 'bg-primary-500 hover:bg-primary-600 text-white')">
          <i class="fas" [class.fa-check]="joinedGroups.has(activeDetailItem.id)"
            [class.fa-user-plus]="!joinedGroups.has(activeDetailItem.id)"></i>
          {{ joinedGroups.has(activeDetailItem.id) ? 'Requested' : 'Join' }}
        </button>
      </div>
    </div>

    <!-- Modal Body Content -->
    <div class="p-6 overflow-y-auto max-h-[70vh] flex flex-col items-center text-center">
      <!-- Avatar / Image -->
      <div class="mb-4">
        @if (activeDetailItem.imageUrl) {
        <img [src]="activeDetailItem.imageUrl"
          class="h-24 w-24 rounded-full object-cover border-2 border-primary-100 shadow-md" />
        } @else {
        <div
          class="h-24 w-24 rounded-full bg-primary-100 text-primary-600 font-bold flex items-center justify-center text-3xl shadow-inner">
          {{activeDetailItem.name | initials}}
        </div>
        }
      </div>

      <!-- Name & Location -->
      <h2 class="text-xl font-bold text-blueGray-900 leading-snug">{{activeDetailItem.name}}</h2>
      @if (activeDetailItem.address?.city; as city) {
      <p class="text-xs text-blueGray-500 mt-1 flex items-center gap-1">
        <i class="fas fa-location-dot text-primary-400"></i> {{city}}
      </p>
      }

      <!-- Type Badge -->
      <span
        class="capitalize px-3 py-0.5 rounded-full bg-blueGray-100 text-blueGray-600 font-semibold text-[10px] tracking-wider mt-3">
        {{activeDetailItem.groupType}}
      </span>

      <!-- Separator -->
      <div class="w-full border-t border-blueGray-100 my-5"></div>

      <!-- Description -->
      <div class="w-full text-left space-y-4 text-xs">
        <div>
          <h4 class="font-bold text-blueGray-700 mb-1 uppercase tracking-wider text-[10px]">Description</h4>
          <p class="text-blueGray-600 leading-relaxed whitespace-pre-line">
            {{activeDetailItem.description || 'No description available for this community.'}}
          </p>
        </div>

        @if (activeDetailItem.email) {
        <div>
          <h4 class="font-bold text-blueGray-700 mb-1 uppercase tracking-wider text-[10px]">Contact Email</h4>
          <a [href]="'mailto:' + activeDetailItem.email" class="text-primary-500 hover:underline">
            {{activeDetailItem.email}}
          </a>
        </div>
        }
      </div>
    </div>
  </div>
</div>
}

<!-- Event Detail Modal/Popup -->
@if (activeDetailEvent) {
<div class="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
  <div
    class="bg-white rounded-2xl border border-blueGray-100 shadow-2xl w-full max-w-lg overflow-hidden flex flex-col relative animate-fade-in">

    <!-- Header Actions row (Close left, Share right) -->
    <div class="flex items-center justify-between p-4 border-b border-blueGray-100 bg-gray-50 flex-none">
      <button (click)="closeEventDetailModal()"
        class="text-blueGray-400 hover:text-blueGray-600 transition-colors p-1.5 focus:outline-none">
        <i class="fas fa-times text-lg"></i>
      </button>

      <div class="flex items-center gap-2">
        <!-- Share WhatsApp button -->
        <a [href]="getEventShareLink(activeDetailEvent)" target="_blank"
          class="px-3.5 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-full text-xs font-semibold shadow-sm flex items-center gap-1.5 transition-colors">
          <i class="fab fa-whatsapp text-sm"></i> Share
        </a>
      </div>
    </div>

    <!-- Modal Body Content -->
    <div class="p-6 overflow-y-auto max-h-[75vh] flex flex-col">
      <!-- Banner Image -->
      @if (activeDetailEvent.bannerImage) {
      <img [src]="activeDetailEvent.bannerImage"
        class="w-full h-44 rounded-xl object-cover mb-4 shadow-sm border border-blueGray-100" />
      }

      <!-- Group hosting info -->
      <div class="flex items-center gap-2 mb-3">
        @if (getGroupForEvent(activeDetailEvent)?.imageUrl || activeDetailEvent.groupImageUrl) {
        <img [src]="getGroupForEvent(activeDetailEvent)?.imageUrl || activeDetailEvent.groupImageUrl"
          class="h-6 w-6 rounded-full object-cover border border-blueGray-200" />
        } @else {
        <i class="fas fa-church text-primary-500 text-sm"></i>
        }
        <span class="text-xs font-semibold text-blueGray-600">
          {{ getGroupForEvent(activeDetailEvent)?.name || activeDetailEvent.groupName || 'Community Event' }}
        </span>
      </div>

      <!-- Title -->
      <h2 class="text-xl font-bold text-blueGray-900 leading-snug mb-2">{{activeDetailEvent.name}}</h2>

      <!-- Date & Time info -->
      <div
        class="flex flex-wrap items-center gap-3 text-xs text-blueGray-600 bg-blueGray-50 p-3 rounded-xl mb-4 border border-blueGray-100">
        <div class="flex items-center gap-1.5">
          <i class="far fa-calendar-alt text-primary-500"></i>
          <span class="font-medium">
            {{ activeDetailEvent.startDateTime | moment: 'MMM D, YYYY' }}
          </span>
        </div>
        @if (activeDetailEvent.startDateTime) {
        <div class="flex items-center gap-1.5">
          <i class="far fa-clock text-primary-500"></i>
          <span class="font-medium">
            {{ activeDetailEvent.startDateTime | moment: 'h:mm A' }}
            @if (activeDetailEvent.endDateTime) {
            - {{ activeDetailEvent.endDateTime | moment: 'h:mm A' }}
            }
          </span>
        </div>
        }
      </div>

      <!-- Description -->
      <div class="space-y-2 text-xs mb-4">
        <h4 class="font-bold text-blueGray-700 uppercase tracking-wider text-[10px]">Event Details</h4>
        <p class="text-blueGray-600 leading-relaxed whitespace-pre-line">
          {{activeDetailEvent.description || 'No additional details provided for this event.'}}
        </p>
      </div>

      <!-- Event Location Map -->
      @if (activeDetailEvent.location?.position) {
      <div class="mt-2 space-y-2">
        <h4 class="font-bold text-blueGray-700 uppercase tracking-wider text-[10px] flex items-center gap-1">
          <i class="fas fa-location-dot text-primary-400"></i>
          {{ activeDetailEvent.location.name || 'Location' }}
        </h4>
        <div class="rounded-xl overflow-hidden border border-blueGray-200 h-[180px]">
          <app-view-location [location]="activeDetailEvent.location" height="180px"></app-view-location>
        </div>
      </div>
      }
    </div>
  </div>
</div>
}